import { useState, useContext, useEffect } from "react";
import clsx from "clsx";

import {
  makeStyles,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  Divider,
  MenuItem,
  IconButton,
  Menu,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";
import NotificationsPopOver from "../components/NotificationsPopOver";
import AnnouncementsPopover from "../components/AnnouncementsPopover";
import BackdropLoading from "../components/BackdropLoading";
import UserModal from "../components/UserModal";
import MainListItems from "./MainListItems";

import { AuthContext } from "../context/Auth/AuthContext";
import { i18n } from "../translate/i18n";
import ColorModeContext from "../layout/themeContext";
import toastError from "../errors/toastError";

import { getSocket } from "../services/socket";

import BrightnessIcon from "../assets/icon/BrightnessIcon";
import UserBorderIcon from "../assets/icon/UserBorderIcon";
import VenttifyIcon from "../assets/icon/VenttifyIcon";
import BackArrow from "../assets/icon/BackArrow";
import MenuIcon from "../assets/icon/MenuIcon";
import { 
  AccountCircle as AccountCircleIcon,
  ExitToApp as ExitToAppIcon 
} from '@material-ui/icons';

const drawerWidth = 270;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    boxShadow: "none",
    backgroundColor: theme.palette.background.default,
    '& .MuiButton-outlinedPrimary': {
      color: theme.mode === 'light' ? '#007aff' : '#FFF',
      border: theme.mode === 'light' ? '1px solid rgba(0 124 102)' : '1px solid rgba(255, 255, 255, 0.5)',
    },
    '& .MuiTab-textColorPrimary.Mui-selected': {
      color: theme.mode === 'light' ? '#007aff' : '#FFF',
    }
  },
  main: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
  },

  toolbar: {
    fontSize:"18px",
    padding: 4,
    color: theme.palette.textPrimary,
    background: theme.palette.tabHeaderBackground,
    boxShadow: "0px 0px 0px 0px",
    borderBottom: "1px solid rgba(127, 127, 127, 0.16)"

  },

  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    padding: "0 4px",
    minHeight: "68px",
    borderBottom:"none",
    color: theme.palette.sideBarIcon,
    background: theme.palette.sidebarBackground,
    boxShadow: "0px 0px 0px 0px",
    justifyContent: "space-between" ,
    [theme.breakpoints.down("sm")]: {
      height: "48px"
    }
  },
  appBar: {
    boxShadow: 'none',
    zIndex: 'auto',  
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: 36,
  },
  menuButtonHidden: {
    display: "none",
  },
  title: {
    flexGrow: 1,
    fontSize: 14,
  },
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    background: theme.palette.sidebarBackground,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9),
    },
  },
  logo: {
    display: "flex",
    textAlign: "center",
    alignItems: "center",
    padding:"15px",
  },
  content: {
    flex: 1,
    display: "flex", 
    flexDirection: "column", 
    overflow: "auto",
    ...theme.scrollbarStyles,
    marginTop: 50, 
    [theme.breakpoints.down('md')]: {
			marginTop: 64 
		},
  },
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
  },
  containerWithScroll: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
}));

const LoggedInLayout = ({ children }) => {
  const classes = useStyles();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading } = useContext(AuthContext);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerVariant, setDrawerVariant] = useState("permanent");
  const { user } = useContext(AuthContext);
console.log(user)
  const theme = useTheme();
  const { colorMode } = useContext(ColorModeContext);
  const greaterThenSm = useMediaQuery(theme.breakpoints.up("sm"));
  const greaterThenLg = useMediaQuery(theme.breakpoints.up("lg"));

  useEffect(() => {
    if (document.body.offsetWidth > 600) {
      setDrawerOpen(true);
    }
  }, []);

  useEffect(() => {
    if (document.body.offsetWidth < 600) {
      setDrawerVariant("temporary");
    } else {
      setDrawerVariant("permanent");
    }
  }, [drawerOpen, document.body.offsetWidth]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const userId = localStorage.getItem("userId");
    const currentToken = localStorage.getItem("token");

    const socket = getSocket(companyId);
    if (!socket) return
    const handleEventAuth = (data) => {
      const sameUser = data?.user?.id === +userId;
      const sameSession = data?.token && currentToken && data.token === currentToken;
      if (sameUser && !sameSession) {
        toastError("Se accedió a su cuenta desde otra computadora.");
        setTimeout(() => {
          localStorage.clear();
          window.location.reload();
        }, 1000);
      }
    }

    socket.on(`company-${companyId}-auth`, handleEventAuth);

    // socket.emit("userStatus");
    // const interval = setInterval(() => {
    //   socket.emit("userStatus");
    // }, 1000 * 60 * 5);

    return () => {
      socket.on(`company-${companyId}-auth`, handleEventAuth);
      // clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!greaterThenLg) {
      setDrawerOpen(false);
    } else {
      setDrawerOpen(true);
    }
  }, [greaterThenLg]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const toggleColorMode = () => {
    colorMode.toggleColorMode();
  }

  const drawerClose = () => {
    if (document.body.offsetWidth < 600) {
      setDrawerOpen(false);
    }
  };

  if (loading) {
    return <BackdropLoading />;
  }

  return (
    <div className={classes.root}>
      <Drawer
        variant={drawerVariant}
        className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
        classes={{
          paper: clsx(
            classes.drawerPaper,
            !drawerOpen && classes.drawerPaperClose
          ),
        }}
        open={drawerOpen}
      >
        <div className={classes.toolbarIcon}>
          <div className={classes.logo}>
            <VenttifyIcon fill={theme.palette.primary.main} width="38px" height="38px" />
            <Typography component="p" variant="subtitle1" style={{ fontWeight: "700", marginLeft:"10px",   fontSize: "18px" }}>
              Venttify
            </Typography>
            </div>
            <div>
              <IconButton onClick={() => setDrawerOpen(!drawerOpen)}>
                <BackArrow  fill="#AEADAC"/>
              </IconButton>
            </div>

        </div>
          <MainListItems drawerClose={drawerClose} drawerOpen={drawerOpen} />
      </Drawer>
      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
      />
      <AppBar
        position="absolute"
        className={clsx(classes.appBar, drawerOpen && classes.appBarShift)}
        color="primary"
      >
        <Toolbar variant="dense" className={classes.toolbar}>
          <IconButton
            edge="start"
            variant="contained"
            aria-label="open drawer"
            onClick={() => setDrawerOpen(!drawerOpen)}
            className={clsx(
              classes.menuButton,
              drawerOpen && classes.menuButtonHidden
            )}
          >
            {drawerOpen ? <BackArrow  fill="#AEADAC"/> : <MenuIcon  fill="#AEADAC"/> }
          </IconButton>
          <Typography
            component="h1"
            variant="h6"
            color="inherit"
            noWrap
            className={classes.title}
            style={{fontSize:"18px", marginLeft: 15}}
          >
          </Typography>

          <IconButton edge="start" onClick={toggleColorMode}>
            {theme.mode === 'dark' ? <BrightnessIcon style={{ color: "white" }} /> : <BrightnessIcon style={{ color: "white" }} />}
          </IconButton>

          {user.id && <NotificationsPopOver />}

              {/* <ChatPopover /> */}
          <AnnouncementsPopover />  
          <div>
            <IconButton
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              variant="contained"
              style={{ color: "white" }}
            >
              <UserBorderIcon fill={theme.palette.primary.main} />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={menuOpen}
              onClose={handleCloseMenu}
              PaperProps={{
                style: {
                  width: '280px',
                  borderRadius: '8px',
                  marginTop: '8px',
                  padding: '8px 0'
                }
              }}
            >
              <div style={{ padding: "16px 20px" }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <Typography 
                    variant="h6" 
                    style={{ 
                      fontWeight: "500",
                      fontSize: '16px',
                      color: theme.palette.text.primary,
                      flex: 1,
                      textAlign: 'center'
                    }}
                  >
                    {user?.name || "Usuario"}
                  </Typography>
                </div>
                <Typography 
                  variant="body2" 
                  style={{ 
                    color: theme.palette.primary.main,
                    fontSize: '14px',
                    marginBottom: '8px',
                    textAlign: 'center'
                  }}
                >
                  {user?.email || "correo@ejemplo.com"}
                </Typography>

                <div style={{ display: 'flex', justifyContent: 'space-between', color: theme.palette.text.secondary, fontSize: '13px' }}>
                  <span>Empresa: {user?.companyId || "N/A"}</span>
                  <span>Versión: 1.0.00</span>
                </div>
              </div>
              <Divider style={{ margin: '8px 0' }} />
              <MenuItem 
                onClick={handleOpenUserModal}
                style={{
                  padding: '12px 20px',
                  fontSize: '14px'
                }}
              >
                <AccountCircleIcon 
                  style={{ 
                    marginRight: '12px',
                    fontSize: '20px',
                    color: '#666'
                  }} 
                />
                {i18n.t("mainDrawer.appBar.user.profile")}
              </MenuItem>
              <MenuItem 
                onClick={handleClickLogout}
                style={{
                  padding: '12px 20px',
                  fontSize: '14px'
                }}
              >
                <ExitToAppIcon 
                  style={{ 
                    marginRight: '12px',
                    fontSize: '20px',
                    color: '#666'
                  }} 
                />
                {i18n.t("mainDrawer.appBar.user.logout")}
              </MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>
      <main className={classes.content}>
        <div/>

        {children ? children : null}
      </main>
    </div>
  );
};

export default LoggedInLayout;
